import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useTheme } from '@/contexts/ThemeContext';
import { Zap, Headphones, Package, Clock, ArrowRight, Sun, Moon, ShoppingBag, Search, CreditCard, Play, HelpCircle, ChevronDown, ChevronUp, BookOpen, Shield, Sparkles, Star, ExternalLink, FolderOpen, FileText } from 'lucide-react';

import Footer from '@/components/Footer';

import { PriceDisplay } from '@/components/PriceDisplay';
import { durationColor } from '@/lib/badgeColors';
import { Separator } from '@/components/ui/separator';

function FAQItem({ question, answer }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border rounded-lg overflow-hidden" data-testid="faq-item">
      <button className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors" onClick={() => setOpen(!open)}>
        <span className="font-medium text-sm pr-4">{question}</span>
        {open ? <ChevronUp className="w-4 h-4 shrink-0 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" />}
      </button>
      {open && <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">{answer}</div>}
    </div>
  );
}

export default function StorefrontHome() {
  const [products, setProducts] = useState([]);
  const [featured, setFeatured] = useState({ hero: null, suggested: [] });
  const [categoryGroups, setCategoryGroups] = useState([]);
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/public/products').then(r => setProducts(r.data)).catch(() => {});
    api.get('/public/featured').then(r => setFeatured(r.data)).catch(() => {});
    api.get('/public/categories/with-products').then(r => setCategoryGroups(r.data)).catch(() => {});
  }, []);

  const guides = products.filter(p => p.type === 'guide');
  const packages = products.filter(p => p.type === 'package');

  return (
    <div className="min-h-screen" data-testid="storefront-home">
      <header className="sticky top-0 z-50 border-b glass-effect bg-background/80">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Wayfinder" className="w-8 h-8 rounded-lg" />
            <span className="font-bold text-lg font-['Outfit']">Wayfinder Guides</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="storefront-theme-toggle">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Link to="/portal"><Button variant="ghost" size="sm"><ShoppingBag className="w-4 h-4 mr-1" />My Guides</Button></Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0C2D3F] via-[#0F3D4D] to-[#1A4A5A]" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 20% 50%, rgba(26,157,165,0.25) 0%, transparent 50%), radial-gradient(circle at 80% 30%, rgba(232,115,90,0.2) 0%, transparent 40%), radial-gradient(circle at 60% 80%, rgba(240,180,41,0.15) 0%, transparent 40%)' }} />

        {/* Floating shapes */}
        <div className="absolute top-16 left-[10%] w-64 h-64 rounded-full bg-[#1A9DA5]/10 blur-3xl animate-pulse" />
        <div className="absolute bottom-10 right-[15%] w-48 h-48 rounded-full bg-[#E8735A]/10 blur-3xl" style={{ animation: 'pulse 4s ease-in-out infinite' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-white/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-white/5" />

        {/* Decorative dots */}
        <div className="absolute top-20 right-[20%] grid grid-cols-5 gap-2 opacity-20">
          {Array.from({ length: 15 }).map((_, i) => <div key={i} className="w-1.5 h-1.5 rounded-full bg-[#1A9DA5]" />)}
        </div>
        <div className="absolute bottom-24 left-[10%] grid grid-cols-4 gap-2 opacity-15">
          {Array.from({ length: 12 }).map((_, i) => <div key={i} className="w-1.5 h-1.5 rounded-full bg-[#F0B429]" />)}
        </div>

        <div className="relative max-w-5xl mx-auto px-4 py-24 md:py-32">
          <div className="grid md:grid-cols-5 gap-10 items-center">
            {/* Text */}
            <div className="md:col-span-3 text-white">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/10 text-sm mb-6">
                <span className="w-2 h-2 rounded-full bg-[#1A9DA5] animate-pulse" />
                <span className="text-white/90">Business Advisor Audio Guides</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight font-['Outfit'] mb-6 leading-[1.1]">
                Real advice from someone<br />
                <span className="relative inline-block">
                  <span className="relative z-10 bg-gradient-to-r from-[#1A9DA5] via-[#23B5BE] to-[#E8735A] bg-clip-text text-transparent">who figured it out the hard way.</span>
                  <span className="absolute bottom-1 left-0 w-full h-3 bg-[#F0B429]/20 -skew-x-3 rounded" />
                </span>
              </h1>
              <p className="text-lg text-white/70 max-w-xl mb-8 leading-relaxed">
                Short audio guides for small business owners who are tired of generic advice. Thomas White spent 18 years running his own business. These are the things that actually helped.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button size="lg" className="bg-[#1A9DA5] hover:bg-[#158D92] text-white px-8 shadow-lg shadow-[#1A9DA5]/25 transition-all hover:shadow-xl hover:shadow-[#1A9DA5]/30 hover:-translate-y-0.5" onClick={() => document.getElementById('guides')?.scrollIntoView({ behavior: 'smooth' })} data-testid="browse-guides-btn">
                  Browse Guides <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10 backdrop-blur" onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}>
                  How It Works
                </Button>
              </div>
              <div className="flex items-center gap-6 mt-8 text-sm text-white/50">
                <span className="flex items-center gap-1.5"><Headphones className="w-4 h-4 text-[#1A9DA5]" />Audio Guides</span>
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#E8735A]" />5-30 min each</span>
                <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-[#F0B429]" />Lifetime Access</span>
              </div>
            </div>

            {/* Visual card stack */}
            <div className="md:col-span-2 hidden md:flex justify-center">
              <div className="relative w-64 h-80">
                {/* Background card */}
                <div className="absolute top-4 -left-3 w-56 h-64 rounded-2xl bg-white/5 border border-white/10 -rotate-6 backdrop-blur" />
                {/* Middle card */}
                <div className="absolute top-2 left-0 w-56 h-64 rounded-2xl bg-white/10 border border-white/10 rotate-3 backdrop-blur" />
                {/* Front card */}
                <div className="absolute top-0 left-2 w-56 h-64 rounded-2xl bg-gradient-to-br from-[#1A9DA5] to-[#158D92] border border-white/20 shadow-2xl shadow-[#1A9DA5]/30 p-5 flex flex-col justify-between">
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center mb-4">
                      <Headphones className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-white font-semibold font-['Outfit'] text-base">Actionable Business Advice</p>
                    <p className="text-white/70 text-xs mt-1">Expert strategies you can implement today</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="h-1 flex-1 rounded bg-white/30" />
                      <div className="h-1 w-8 rounded bg-[#F0B429]" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Play className="w-3.5 h-3.5 text-white" />
                      <div className="h-1 flex-1 rounded bg-white/20" />
                      <span className="text-[10px] text-white/50">15:00</span>
                    </div>
                  </div>
                </div>
                {/* Floating badge */}
                <div className="absolute -bottom-3 -right-3 px-3 py-1.5 rounded-full bg-[#E8735A] text-white text-xs font-medium shadow-lg shadow-[#E8735A]/30">
                  {guides.length}+ Guides
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {(featured.hero || featured.suggested.length > 0) && (
        <section className="py-16 px-4" data-testid="featured-section">
          <div className="max-w-6xl mx-auto space-y-10">
            {/* Hero Featured */}
            {featured.hero && (
              <div
                className="relative rounded-2xl overflow-hidden cursor-pointer group"
                onClick={() => navigate(`/${featured.hero.type === 'package' ? 'packages' : 'guides'}/${featured.hero.slug}`)}
                data-testid="featured-hero-card"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#0C2D3F] via-[#0F3D4D] to-[#1A4A5A]" />
                <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 80% 50%, rgba(26,157,165,0.2) 0%, transparent 50%)' }} />
                <div className="absolute top-8 right-[10%] w-40 h-40 rounded-full bg-[#E8735A]/10 blur-3xl" />
                <div className="relative p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-6">
                  <div className="flex-1 text-white">
                    <Badge className="bg-[#E8735A] text-white mb-3 shadow-lg shadow-[#E8735A]/20">
                      <Star className="w-3 h-3 mr-1 fill-white" />Featured
                    </Badge>
                    <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] mb-2 group-hover:text-[#1A9DA5] transition-colors">{featured.hero.title}</h2>
                    <p className="text-white/60 mb-4 max-w-xl text-sm leading-relaxed">{featured.hero.short_description}</p>
                    <div className="flex items-center gap-4 flex-wrap">
                      <PriceDisplay product={featured.hero} size="lg" />
                      {featured.hero.duration_minutes && (
                        <Badge variant="outline" className="text-white/70 border-white/20">
                          <Clock className="w-3 h-3 mr-1" />{featured.hero.duration_minutes} min
                        </Badge>
                      )}
                      {featured.hero.category && <Badge variant="outline" className="text-white/70 border-white/20">{featured.hero.category}</Badge>}
                    </div>
                  </div>
                  <Button size="lg" className="bg-[#1A9DA5] hover:bg-[#158D92] text-white shadow-lg shadow-[#1A9DA5]/25 shrink-0">
                    View Details <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Suggested Products */}
            {featured.suggested.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-5">
                  <Sparkles className="w-4 h-4 text-[#1A9DA5]" />
                  <h2 className="text-xl font-bold font-['Outfit']">Recommended for You</h2>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {featured.suggested.map(p => (
                    <Card
                      key={p.id}
                      className="group border-2 border-[#1A9DA5]/10 hover:border-[#1A9DA5]/40 hover:shadow-lg transition-all cursor-pointer"
                      onClick={() => navigate(`/${p.type === 'package' ? 'packages' : 'guides'}/${p.slug}`)}
                      data-testid={`suggested-card-${p.id}`}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-lg bg-[#1A9DA5]/10 flex items-center justify-center shrink-0">
                            {p.type === 'package' ? <Package className="w-5 h-5 text-[#1A9DA5]" /> : <Headphones className="w-5 h-5 text-[#1A9DA5]" />}
                          </div>
                          <Badge variant="outline" className="text-xs bg-[#1A9DA5]/5 text-[#1A9DA5] border-[#1A9DA5]/20">
                            <Sparkles className="w-2.5 h-2.5 mr-1" />Pick
                          </Badge>
                        </div>
                        <h3 className="text-base font-semibold font-['Outfit'] mb-1 group-hover:text-[#1A9DA5] transition-colors">{p.title}</h3>
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{p.short_description}</p>
                        <div className="flex items-center justify-between">
                          <PriceDisplay product={p} size="md" />
                          {p.duration_minutes && (
                            <div className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${durationColor(p.duration_minutes).bg} ${durationColor(p.duration_minutes).text}`}>
                              <Clock className="w-3 h-3" />{p.duration_minutes} min
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <section id="guides" className="py-16 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto space-y-16">
          {categoryGroups.length > 0 ? (
            categoryGroups.map((cat, ci) => (
              <div key={cat.id} data-testid={`category-block-${cat.id}`}>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold font-['Outfit']">{cat.name}</h2>
                    {cat.short_description && <p className="text-sm text-muted-foreground mt-1">{cat.short_description}</p>}
                  </div>
                  {cat.slug && (
                    <Button variant="outline" size="sm" onClick={() => navigate(`/category/${cat.slug}`)} className="shrink-0">
                      View All <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {(cat.products || []).map(p => (
                    <Card key={p.id} className={`group hover:shadow-lg transition-all duration-300 cursor-pointer ${p.type === 'package' ? 'border-2 border-[#1A9DA5]/30 hover:border-[#1A9DA5]' : 'hover:border-[#1A9DA5]/50'}`} onClick={() => navigate(`/${p.type === 'package' ? 'packages' : 'guides'}/${p.slug}`)}>
                      <CardContent className="p-6">
                        <div className="w-12 h-12 rounded-xl bg-[#1A9DA5]/10 flex items-center justify-center mb-4">
                          {p.type === 'package' ? <Package className="w-6 h-6 text-[#1A9DA5]" /> : p.type === 'pdf' ? <FileText className="w-6 h-6 text-[#E8735A]" /> : <Headphones className="w-6 h-6 text-[#1A9DA5]" />}
                        </div>
                        {p.type === 'package' && p.savings_display && <Badge className="bg-[#F0B429] text-white mb-2">{p.savings_display}</Badge>}
                        <h3 className="text-lg font-semibold font-['Outfit'] mb-2 group-hover:text-[#1A9DA5] transition-colors">{p.title}</h3>
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{p.short_description}</p>
                        <div className="flex items-center justify-between">
                          <PriceDisplay product={p} size="md" />
                          {p.duration_minutes && (
                            <div className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${durationColor(p.duration_minutes).bg} ${durationColor(p.duration_minutes).text}`}>
                              <Clock className="w-3 h-3" />{p.duration_minutes} min
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {ci < categoryGroups.length - 1 && <Separator className="mt-16" />}
              </div>
            ))
          ) : (
            /* Fallback: show all products ungrouped */
            <>
              <h2 className="text-3xl font-bold font-['Outfit'] mb-8">Audio Guides</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map(p => (
                  <Card key={p.id} className="group hover:border-[#1A9DA5]/50 hover:shadow-lg transition-all cursor-pointer" onClick={() => navigate(`/${p.type === 'package' ? 'packages' : 'guides'}/${p.slug}`)}>
                    <CardContent className="p-6">
                      <div className="w-12 h-12 rounded-xl bg-[#1A9DA5]/10 flex items-center justify-center mb-4">
                        {p.type === 'package' ? <Package className="w-6 h-6 text-[#1A9DA5]" /> : <Headphones className="w-6 h-6 text-[#1A9DA5]" />}
                      </div>
                      <h3 className="text-lg font-semibold font-['Outfit'] mb-2 group-hover:text-[#1A9DA5] transition-colors">{p.title}</h3>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{p.short_description}</p>
                      <div className="flex items-center justify-between">
                        <PriceDisplay product={p} size="md" />
                        {p.duration_minutes && (
                          <div className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${durationColor(p.duration_minutes).bg} ${durationColor(p.duration_minutes).text}`}>
                            <Clock className="w-3 h-3" />{p.duration_minutes} min
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 px-4 bg-muted/30" id="how-it-works" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-3 bg-[#E8735A]/10 text-[#E8735A] border-[#E8735A]/20">How It Works</Badge>
            <h2 className="text-3xl font-bold font-['Outfit']">Get Started in 3 Easy Steps</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: 1, icon: Search, color: '#1A9DA5', title: 'Browse & Choose', desc: 'Explore our library of audio guides or pick a bundle that covers multiple topics. Each guide is designed for a specific business challenge.' },
              { step: 2, icon: CreditCard, color: '#E8735A', title: 'Purchase Securely', desc: 'Complete your purchase through our secure Stripe checkout. You\'ll get instant access via email with a one-click login link to your library.' },
              { step: 3, icon: Play, color: '#F0B429', title: 'Listen & Implement', desc: 'Access your guides anytime from your personal library. Listen on the go, download PDFs if included, and start implementing right away.' },
            ].map(s => (
              <div key={s.step} className="text-center">
                <div className="relative mx-auto mb-4 w-16 h-16">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${s.color}15` }}>
                    <s.icon className="w-7 h-7" style={{ color: s.color }} />
                  </div>
                  <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-foreground text-background text-xs font-bold flex items-center justify-center">{s.step}</div>
                </div>
                <h3 className="text-lg font-semibold font-['Outfit'] mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-16 px-4" data-testid="about-section">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-5 gap-10 items-start">
            <div className="md:col-span-3">
              <Badge className="mb-3 bg-[#1A9DA5]/10 text-[#1A9DA5] border-[#1A9DA5]/20">About Wayfinder</Badge>
              <h2 className="text-3xl font-bold font-['Outfit'] mb-6">Built by someone who's been through it.</h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>My name is Thomas White. I ran a small service business for 18 years. I made most of the mistakes you're probably making right now, and I figured things out slowly, usually at some cost.</p>
                <p>What I kept noticing was that good advice existed, but it was buried in long courses, expensive coaches, or generic blog posts that didn't actually apply to a real business with real pressures.</p>
                <p>So I started writing things down. The stuff that actually worked. Short, practical, no fluff. That became Wayfinder Guides.</p>
                <p className="font-medium text-foreground">These guides are not theory. They're what I'd tell a friend who just called me stressed about the same problem I already solved.</p>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-6">
                {[
                  { icon: Headphones, text: 'Listen Anywhere' },
                  { icon: Clock, text: '5-30 Min Each' },
                  { icon: BookOpen, text: 'PDF Included' },
                  { icon: Shield, text: 'Lifetime Access' },
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <f.icon className="w-4 h-4 text-[#1A9DA5]" />
                    <span>{f.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="md:col-span-2 flex flex-col items-center">
              <img src="/thomas-white.png" alt="Thomas White" className="w-48 h-48 rounded-full object-cover border-4 border-[#1A9DA5]/20 shadow-xl mb-4" />
              <h3 className="text-lg font-bold font-['Outfit'] text-center">Thomas White</h3>
              <p className="text-sm text-muted-foreground text-center">Founder, Wayfinder Guides</p>
              <div className="mt-4 space-y-2 w-full">
                {[
                  '18 years running a small business',
                  'Practical advice, not theory',
                  'New guides added regularly',
                ].map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Sparkles className="w-3.5 h-3.5 text-[#E8735A] shrink-0" />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-muted/30" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-3 bg-[#F0B429]/10 text-[#F0B429] border-[#F0B429]/20">FAQ</Badge>
            <h2 className="text-3xl font-bold font-['Outfit']">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            <FAQItem
              question="What exactly are Wayfinder Guides?"
              answer="They are short, focused audio lessons (typically 5-20 minutes) designed for busy business owners. Each guide covers a specific topic with actionable steps you can implement immediately. Some guides also include a downloadable PDF companion."
            />
            <FAQItem
              question="How do I access my guides after purchase?"
              answer="After your purchase is complete, you'll receive an email with a one-click login link. Click it to access your personal library where all your purchased guides are available to play instantly. You can also access your library anytime from the 'My Guides' link in the navigation."
            />
            <FAQItem
              question="Can I listen on my phone?"
              answer="Yes! Your library is fully responsive and works great on mobile browsers. Just open your library link on any device and start listening — no app download required."
            />
            <FAQItem
              question="What's included in a bundle/package?"
              answer="Bundles combine multiple individual guides at a discounted price. You get lifetime access to all included guides, and any PDFs that come with them. It's the best value if you're looking to level up across multiple areas."
            />
            <FAQItem
              question="Is there a refund policy?"
              answer="Yes. If you go through a guide and don't find it useful, email us and we'll refund you. No lengthy process. These guides either help you or they don't, and we'd rather you get your money back than feel like you got burned by something that didn't deliver."
            />
            <FAQItem
              question="How long do I have access?"
              answer="Lifetime! Once you purchase a guide or bundle, it's yours forever. Access it anytime from your personal library."
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
